import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Drawer, Typography, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material/styles';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import type {
  AdvisoryAnswerEvidenceValidation,
  AdvisoryEvidenceRegisterV01,
  AdvisoryResearchContextV01,
  AdvisorySourceReferenceV01,
} from './advisoryProvenance';
import type { AdvisoryBriefDocumentInputV01 } from './types';
import { QuoteTrimNotice, SourceQuotes } from './AdvisorySourceQuotes';
import { AdvisorySourceDetails, type AdvisorySourceUse } from './AdvisorySourceDetails';
import type { AdvisorySourcePassageLoader } from './advisorySourcePassage';
import type { AdvisoryQuoteReference, AdvisoryQuoteSelection } from './advisoryQuoteAnchors';
import type { AdvisoryTableSourceReference } from './advisoryTableSources';
import { AdvisoryAuditPanel, type AdvisoryAuditPanelProps } from './AdvisoryAuditPanel';
import { advisorySourceCheck } from './advisoryReportEvidence';
import { AdvisorySourceHeading } from './AdvisorySourceHeading';
import { AdvisorySourceMetadata } from './AdvisorySourceMetadata';
import { AdvisoryEvidenceCallout, quoteCheckTone } from './AdvisoryEvidenceCallout';
import { AdvisorySidebarQuote } from './AdvisorySidebarQuote';

type ResearchStatus = NonNullable<AdvisoryBriefDocumentInputV01['researchContextStatus']>;

export interface AdvisorySourcesDrawerProps {
  open: boolean;
  view?: 'sources' | 'audit';
  reportScope?: boolean;
  chapterNumber?: number;
  initialSelection?: AdvisoryQuoteSelection;
  quoteReferences?: readonly AdvisoryQuoteReference[];
  tableReferences?: readonly AdvisoryTableSourceReference[];
  question: string;
  answerEvidence: AdvisoryAnswerEvidenceValidation | null;
  researchContext: AdvisoryResearchContextV01 | null | undefined;
  researchStatus: ResearchStatus | undefined;
  onClose: () => void;
  auditPanel?: AdvisoryAuditPanelProps;
  sourceUses?: (source: AdvisorySourceReferenceV01) => readonly AdvisorySourceUse[];
  onNavigateToAnswer?: (answerKey: string) => void;
  loadSourcePassage?: AdvisorySourcePassageLoader;
}

function SourceRow({
  source,
  quoteReferences,
  tableReferences,
  onInspect,
}: {
  source: AdvisorySourceReferenceV01;
  quoteReferences?: readonly AdvisoryQuoteReference[];
  tableReferences?: readonly AdvisoryTableSourceReference[];
  onInspect?: (
    source: AdvisorySourceReferenceV01,
    origin: HTMLElement,
    quoteIndex?: number
  ) => void;
}) {
  const { t } = useUntypedTranslation();
  const checkStatus = advisorySourceCheck(source);
  const hasQuotes = Boolean(source.quotes?.length) && !tableReferences?.length;
  const content = (
    <AdvisorySourceHeading references={quoteReferences} tableReferences={tableReferences}>
      {onInspect ? (
        <Button
          onClick={event => onInspect(source, event.currentTarget)}
          sx={theme => ({
            color: theme.colors.inkPrimary,
            fontWeight: 500,
            fontSize: 16,
            lineHeight: 1.5,
            textTransform: 'none',
            textAlign: 'left',
            justifyContent: 'flex-start',
            padding: 0,
            minWidth: 0,
            alignItems: 'flex-start',
            flex: 1,
            overflowWrap: 'anywhere',
          })}
        >
          <Box component="span" sx={{ minWidth: 0 }}>
            {source.title ?? t('advisory:brief.sources.untitled')}
          </Box>
          <span aria-hidden="true" style={{ marginLeft: 8, flexShrink: 0 }}>
            ›
          </span>
        </Button>
      ) : (
        <Typography sx={{ fontWeight: 500 }}>
          {source.title ?? t('advisory:brief.sources.untitled')}
        </Typography>
      )}
      {tableReferences?.length ? (
        <Typography sx={{ fontSize: 12 }}>{t('advisory:brief.sources.tableSource')}</Typography>
      ) : null}
      <AdvisorySourceMetadata
        source={source}
        checkStatus={onInspect && !tableReferences?.length ? checkStatus : undefined}
      />
      {hasQuotes ? (
        <>
          {source.quotes?.map((quote, index) => (
            <Box key={index} data-source-quote-index={index} data-quote-status={quote.status}>
              <AdvisorySidebarQuote>{quote.text}</AdvisorySidebarQuote>
            </Box>
          ))}
          {source.quotesUnavailable ? (
            <Typography role="status" sx={{ fontSize: 12, marginTop: 1 }}>
              {t('advisory:brief.sources.quotes.unavailable')}
            </Typography>
          ) : null}
          <QuoteTrimNotice source={source} />
        </>
      ) : !tableReferences?.length && source.quotes !== undefined ? (
        <Box sx={{ marginTop: 2 }}>
          <SourceQuotes key={source.key} source={source} />
        </Box>
      ) : null}
    </AdvisorySourceHeading>
  );
  return (
    <Box
      component="li"
      data-source-id={source.id ?? undefined}
      data-source-kind={source.kind}
      sx={theme => ({
        listStyle: 'none',
        ...(!hasQuotes
          ? {
              borderTop: `1px solid ${theme.visualPrimitives.borderLight}`,
              paddingY: 2,
            }
          : {}),
      })}
    >
      {hasQuotes ? (
        <AdvisoryEvidenceCallout tone={quoteCheckTone(checkStatus)} plain>
          {content}
        </AdvisoryEvidenceCallout>
      ) : (
        content
      )}
    </Box>
  );
}

function Register({
  register,
  quoteReferences,
  tableReferences,
  onInspect,
}: {
  register: AdvisoryEvidenceRegisterV01;
  quoteReferences?: readonly AdvisoryQuoteReference[];
  tableReferences?: readonly AdvisoryTableSourceReference[];
  onInspect?: (
    source: AdvisorySourceReferenceV01,
    origin: HTMLElement,
    quoteIndex?: number
  ) => void;
}) {
  const { t } = useUntypedTranslation();
  return (
    <>
      {register.sources.length === 0 ? (
        <Typography sx={theme => ({ color: theme.colors.inkSecondary })}>
          {t('advisory:brief.sources.empty')}
        </Typography>
      ) : (
        <Box component="ul" sx={{ margin: 0, padding: 0 }}>
          {register.sources.map(source => (
            <SourceRow
              key={source.key}
              source={source}
              quoteReferences={quoteReferences?.filter(
                reference => reference.source.key === source.key
              )}
              tableReferences={tableReferences?.filter(
                reference => reference.source.key === source.key
              )}
              onInspect={onInspect}
            />
          ))}
        </Box>
      )}
      {register.truncated === true ? (
        <Typography
          role="status"
          data-testid="advisory-sources-truncated"
          sx={theme => ({ color: theme.colors.vermilion, marginTop: 2 })}
        >
          {t('advisory:brief.sources.truncated')}
        </Typography>
      ) : null}
      {register.malformedSources > 0 ? (
        <Typography
          role="status"
          data-testid="advisory-sources-malformed"
          sx={theme => ({ color: theme.colors.vermilion, marginTop: 2 })}
        >
          {t('advisory:brief.sources.someUnavailable')}
        </Typography>
      ) : null}
    </>
  );
}

function ClaimResolution({ context }: { context: AdvisoryResearchContextV01 }) {
  const { t } = useUntypedTranslation();
  if (!context.claimResolution || context.claimResolution.state === 'sourced') return null;
  return (
    <Typography
      component="p"
      data-claim-resolution={context.claimResolution.state}
      sx={theme => ({ color: theme.colors.inkSecondary, marginTop: 0, marginBottom: 2 })}
    >
      {t(`advisory:brief.sources.researchState.${context.claimResolution.state}`)}
    </Typography>
  );
}

function DrawerSection({
  title,
  children,
  first = false,
}: {
  title: string;
  children: React.ReactNode;
  first?: boolean;
}) {
  return (
    <Box component="section" sx={{ marginTop: first ? 0 : 4 }}>
      <Typography
        component="h3"
        sx={theme => ({
          color: theme.colors.inkPrimary,
          fontSize: 16,
          fontWeight: 500,
          marginBottom: 1.5,
        })}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function ResearchContents({
  context,
  status,
}: {
  context: AdvisoryResearchContextV01 | null | undefined;
  status: ResearchStatus | undefined;
}) {
  const { t } = useUntypedTranslation();
  if (status === 'ready' && context) {
    return (
      <>
        <ClaimResolution context={context} />
        <Register register={context.register} />
      </>
    );
  }
  if (status === 'loading') {
    return (
      <Typography sx={theme => ({ color: theme.colors.inkSecondary })}>
        {t('advisory:brief.sources.loading')}
      </Typography>
    );
  }
  return (
    <Typography role="status" sx={theme => ({ color: theme.colors.inkSecondary })}>
      {t('advisory:brief.sources.unavailable')}
    </Typography>
  );
}

/** Recorded answer quotes and audit findings, beside the opening research register. */
export function AdvisorySourcesDrawer({
  open,
  view = 'sources',
  reportScope = false,
  chapterNumber,
  initialSelection,
  quoteReferences,
  tableReferences,
  question,
  answerEvidence,
  researchContext,
  researchStatus,
  onClose,
  auditPanel,
  sourceUses,
  onNavigateToAnswer,
  loadSourcePassage,
}: AdvisorySourcesDrawerProps) {
  const { t } = useUntypedTranslation();
  const theme = useTheme();
  const desktop = useMediaQuery('(min-width: 768px)');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [sourceKey, setSourceKey] = useState<string | null>(initialSelection?.sourceKey ?? null);
  const [quoteIndex, setQuoteIndex] = useState<number | undefined>(initialSelection?.quoteIndex);
  const sourceOriginRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const originScrollTopRef = useRef(0);
  const indexHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const detailHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const previousSourceKey = useRef<string | null>(null);
  const answer = answerEvidence?.ok ? answerEvidence.value : null;
  const answerInvalid = answerEvidence && !answerEvidence.ok;
  const anchor = desktop ? 'right' : 'bottom';
  const selectedSource = answer?.sources.find(source => source.key === sourceKey) ?? null;
  const selectedSourceUses = useMemo(
    () => (selectedSource ? sourceUses?.(selectedSource) ?? [] : []),
    [selectedSource, sourceUses]
  );
  const sourceReferences = quoteReferences?.filter(
    reference => reference.source.key === selectedSource?.key
  );
  const activeKey = selectedSource?.key ?? null;
  const auditView = view === 'audit';
  const eyebrowSx = {
    color: theme.colors.inkTertiary,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0.11em',
    margin: 0,
    textTransform: 'uppercase',
  };
  const inspectSource = (
    source: AdvisorySourceReferenceV01,
    origin: HTMLElement,
    index?: number
  ) => {
    setQuoteIndex(index);
    sourceOriginRef.current = origin;
    originScrollTopRef.current = contentRef.current?.scrollTop ?? 0;
    setSourceKey(source.key);
  };
  useLayoutEffect(() => {
    if (activeKey) {
      detailHeadingRef.current?.focus({ preventScroll: true });
      if (contentRef.current) contentRef.current.scrollTop = 0;
    } else if (previousSourceKey.current) {
      (sourceOriginRef.current ?? indexHeadingRef.current)?.focus({ preventScroll: true });
      if (contentRef.current) contentRef.current.scrollTop = originScrollTopRef.current;
    }
    previousSourceKey.current = activeKey;
  }, [activeKey]);
  const goBack = () => {
    setSourceKey(null);
    setQuoteIndex(undefined);
  };

  return (
    <Drawer
      anchor={anchor}
      disableRestoreFocus={!!initialSelection}
      aria-labelledby="advisory-sources-title"
      open={open}
      onClose={onClose}
      transitionDuration={reducedMotion ? 0 : parseInt(theme.visualPrimitives.tFast, 10)}
      // The Brief mounts the drawer already open, so explicitly enable its entrance.
      SlideProps={{ appear: !reducedMotion, easing: theme.visualPrimitives.ease }}
      BackdropProps={{ easing: theme.visualPrimitives.ease }}
      PaperProps={{
        'data-testid': 'advisory-sources-drawer',
        role: 'dialog',
        'aria-labelledby': 'advisory-sources-title',
        'aria-modal': true,
        'data-advisory-sources-anchor': anchor,
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            if (selectedSource && sourceOriginRef.current) goBack();
            else onClose();
          }
        },
        sx: {
          width: desktop ? 'min(460px, 94vw)' : '100%',
          maxHeight: desktop ? '100%' : '82vh',
          padding: 0,
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
          paddingX: 3.5,
          paddingY: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          component="nav"
          aria-label={t('advisory:brief.sources.trail')}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
        >
          {selectedSource ? (
            <>
              <Button
                size="small"
                onClick={goBack}
                startIcon={<ArrowBackIcon />}
                sx={{ flexShrink: 0 }}
              >
                {t(auditView ? 'advisory:brief.audit.title' : 'advisory:brief.sources.back')}
              </Button>
              <span aria-hidden="true">›</span>
              <Typography noWrap sx={{ fontSize: 13 }}>
                {selectedSource.title ?? t('advisory:brief.sources.untitled')}
              </Typography>
            </>
          ) : (
            <Typography
              component={reportScope ? 'h2' : 'span'}
              ref={reportScope ? indexHeadingRef : undefined}
              id={reportScope ? 'advisory-sources-title' : undefined}
              tabIndex={reportScope ? -1 : undefined}
              aria-current="location"
              sx={eyebrowSx}
            >
              {t(auditView ? 'advisory:brief.audit.title' : 'advisory:brief.sources.eyebrow')}
            </Typography>
          )}
        </Box>
        <Button onClick={onClose} aria-label={t('advisory:brief.sources.close')} size="small">
          {t('advisory:brief.sources.close')}
        </Button>
      </Box>
      <Box
        ref={contentRef}
        data-testid="advisory-sources-content"
        sx={theme => ({
          overflowY: 'auto',
          // Keep quote widths unchanged when an expanded passage requires scrolling.
          scrollbarGutter: 'stable',
          overscrollBehavior: 'contain',
          padding: 3.5,
          paddingBottom: 6,
          color: theme.colors.inkPrimary,
        })}
      >
        <Box hidden={!!selectedSource}>
          {!reportScope ? (
            <Box
              sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 2 }}
            >
              <Box>
                {chapterNumber !== undefined ? (
                  <Typography component="p" sx={eyebrowSx}>
                    {t('advisory:brief.chapterMarker', {
                      position: String(chapterNumber).padStart(2, '0'),
                    })}
                  </Typography>
                ) : null}
                <Typography
                  component="h2"
                  ref={indexHeadingRef}
                  tabIndex={-1}
                  id={selectedSource ? undefined : 'advisory-sources-title'}
                  sx={{
                    fontSize: 24,
                    fontWeight: 500,
                    marginTop: chapterNumber === undefined ? 0 : 1,
                  }}
                >
                  {question}
                </Typography>
              </Box>
            </Box>
          ) : null}

          {auditView && auditPanel ? (
            <Box sx={{ marginTop: reportScope ? 0 : 3 }}>
              <AdvisoryAuditPanel
                {...auditPanel}
                quoteReferences={quoteReferences}
                onOpenSource={(reference, origin) => inspectSource(reference.source, origin)}
              />
            </Box>
          ) : null}

          {!auditView && (answer || answerInvalid) ? (
            <DrawerSection
              first={reportScope}
              title={t(
                reportScope
                  ? 'advisory:brief.sources.reportEvidence'
                  : 'advisory:brief.sources.answerEvidence'
              )}
            >
              {answer ? (
                <Register
                  register={answer}
                  quoteReferences={quoteReferences}
                  tableReferences={tableReferences}
                  onInspect={inspectSource}
                />
              ) : (
                <Typography role="status" sx={theme => ({ color: theme.colors.inkSecondary })}>
                  {t('advisory:brief.sources.answerUnavailable')}
                </Typography>
              )}
            </DrawerSection>
          ) : null}

          {!auditView ? (
            <DrawerSection title={t('advisory:brief.sources.researchContext')}>
              <ResearchContents context={researchContext} status={researchStatus} />
            </DrawerSection>
          ) : null}
        </Box>
        {selectedSource ? (
          <AdvisorySourceDetails
            key={JSON.stringify([
              selectedSource.key,
              quoteIndex,
              selectedSource.documentId,
              selectedSource.chunkId,
              selectedSource.collectionIds,
            ])}
            source={selectedSource}
            quoteReferences={sourceReferences}
            tableReferences={tableReferences?.filter(
              reference => reference.source.key === selectedSource.key
            )}
            quoteIndex={quoteIndex}
            headingRef={detailHeadingRef}
            uses={selectedSourceUses}
            onOpenAnswer={onNavigateToAnswer}
            loadSourcePassage={loadSourcePassage}
          />
        ) : null}
      </Box>
    </Drawer>
  );
}
